// Canvas preview: separated composite + optional mask overlays.
//
// Draws at the working-copy resolution (<= 1024 px) and scales into the
// viewport; the full-resolution artwork is only used for export.
//
// Repeat preview follows the Photoshop "Pattern Preview / Offset" idea:
// the pattern is tiled in both directions and pans with wrap-around, so the
// seams stay continuous while dragging (state lives in App, this component
// only reports deltas).
//
// A hover preview from the Auto Variants panel temporarily replaces the base
// composite without touching the real separation result.

import React, { useEffect, useRef } from 'react';
import type { PaletteColor, RepeatOptions } from '@imagelayerdecomposer/core';
import { labToSrgb } from '@imagelayerdecomposer/core';
import type { RepeatView } from './RepeatPanel';

export interface HoverPreview {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

interface CanvasPreviewProps {
  image: HTMLImageElement;
  previewData: Uint8ClampedArray | null;
  previewWidth: number;
  previewHeight: number;
  hoverPreview: HoverPreview | null;
  masks: Uint8ClampedArray[];
  maskWidth: number;
  maskHeight: number;
  visibleMasks: boolean[];
  colors: PaletteColor[];
  repeat: RepeatOptions;
  repeatView: RepeatView;
  activeTab: 'tile' | 'repeat';
  onRepeatChange: (patch: Partial<RepeatOptions>) => void;
}

function wrap(value: number, mod: number): number {
  if (!Number.isFinite(mod) || mod <= 0) return 0;
  return ((value % mod) + mod) % mod;
}

/** Colored overlay for one alpha mask: RGB = palette color, ALPHA = mask. */
const overlayCache = new WeakMap<Uint8ClampedArray, Map<string, HTMLCanvasElement>>();

function getOverlayCanvas(
  mask: Uint8ClampedArray,
  maskW: number,
  maskH: number,
  hex: string,
  rgb: { r: number; g: number; b: number }
): HTMLCanvasElement {
  let perMask = overlayCache.get(mask);
  if (!perMask) {
    perMask = new Map();
    overlayCache.set(mask, perMask);
  }
  const cached = perMask.get(hex);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = maskW;
  canvas.height = maskH;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(maskW, maskH);
  const out = img.data;
  for (let i = 0, p = 0; i < maskW * maskH; i++, p += 4) {
    out[p] = rgb.r;
    out[p + 1] = rgb.g;
    out[p + 2] = rgb.b;
    out[p + 3] = mask[p]; // gray value -> alpha
  }
  ctx.putImageData(img, 0, 0);
  perMask.set(hex, canvas);
  return canvas;
}

function hexFromLab(lab: { L: number; a: number; b: number }): string {
  const s = labToSrgb(lab);
  const to2 = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${to2(s.r)}${to2(s.g)}${to2(s.b)}`;
}

function rgbaCanvas(data: Uint8ClampedArray, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(width, height);
  img.data.set(data);
  ctx.putImageData(img, 0, 0);
  return canvas;
}

export default function CanvasPreview({
  image,
  previewData,
  previewWidth,
  previewHeight,
  hoverPreview,
  masks,
  maskWidth,
  maskHeight,
  visibleMasks,
  colors,
  repeat,
  repeatView,
  activeTab,
  onRepeatChange,
}: CanvasPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tilesRef = useRef(1);
  const dragRef = useRef<{ x: number; y: number; dx: number; dy: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    const hasPreview = !!previewData && previewWidth > 0 && previewHeight > 0;

    // Work in preview pixel space when available: the separated composite is
    // the actual artwork, and the raw upload is only a fallback.
    const workW = hasPreview ? previewWidth : image.width;
    const workH = hasPreview ? previewHeight : image.height;
    canvas.width = workW;
    canvas.height = workH;

    const displayW = Math.min(860, workW);
    const displayH = Math.max(1, Math.round((displayW / workW) * workH));
    canvas.style.width = `${displayW}px`;
    canvas.style.height = `${displayH}px`;

    ctx.clearRect(0, 0, workW, workH);

    // ---- Base: separated composite (or raw image before separation) ----
    let base: HTMLCanvasElement | HTMLImageElement = image;
    if (hoverPreview) {
      base = rgbaCanvas(hoverPreview.data, hoverPreview.width, hoverPreview.height);
    } else if (hasPreview) {
      base = rgbaCanvas(previewData!, previewWidth, previewHeight);
    }

    if (activeTab === 'tile') {
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(base, 0, 0, workW, workH);
      tilesRef.current = 1;
    } else {
      // ---- Repeat preview ----
      const tileW = repeat.tileW || image.width || 1;
      const tileH = repeat.tileH || image.height || 1;

      // Explicit 2x2 / 3x3 / 4x4, or "Fit": as many tiles as fit while each
      // stays comfortably visible (>= 240 working px, capped at 6).
      const tiles =
        repeatView.count === 'fit'
          ? Math.max(2, Math.min(6, Math.floor(workW / 240)))
          : Math.max(1, repeatView.count);
      tilesRef.current = tiles;

      const cellW = workW / tiles;
      const cellH = workH / tiles;

      // Pan is stored in TILE pixels; convert to a fraction, then to view px.
      const panX = wrap(repeat.dx, tileW) / tileW;
      const panY = wrap(repeat.dy, tileH) / tileH;
      const ox = panX * cellW;
      const oy = panY * cellH;

      ctx.imageSmoothingEnabled = true;

      // One extra row/column on each side covers the wrapped edges.
      for (let r = -1; r <= tiles; r++) {
        for (let c = -1; c <= tiles; c++) {
          let x = c * cellW - ox;
          let y = r * cellH - oy;
          // Wrap-around shifts: brick = odd rows shift horizontally,
          // half-drop = odd columns shift vertically.
          if (repeat.type === 'brick' && wrap(r, 2) !== 0) x += cellW * repeat.ratio;
          if (repeat.type === 'half-drop' && wrap(c, 2) !== 0) y += cellH * repeat.ratio;
          if (x > workW || y > workH || x + cellW < 0 || y + cellH < 0) continue;
          ctx.drawImage(base, x, y, cellW, cellH);
        }
      }

      // ---- Seam check guides ----
      if (repeatView.seamCheck) {
        ctx.save();
        ctx.strokeStyle = 'rgba(255, 90, 90, 0.85)';
        ctx.lineWidth = Math.max(1, workW / 1024);
        ctx.setLineDash([8, 5]);
        ctx.beginPath();
        for (let c = -1; c <= tiles; c++) {
          const x = c * cellW - ox;
          ctx.moveTo(x, 0);
          ctx.lineTo(x, workH);
        }
        for (let r = -1; r <= tiles; r++) {
          const y = r * cellH - oy;
          ctx.moveTo(0, y);
          ctx.lineTo(workW, y);
        }
        ctx.stroke();
        ctx.restore();
      }
    }

    // ---- Mask overlays (only when the eye is on) ----
    for (let k = 0; k < masks.length; k++) {
      if (!visibleMasks[k]) continue;
      const mask = masks[k];
      if (!mask) continue;
      const lab = colors[k]?.lab ?? { L: 100, a: 0, b: 0 };
      const hex = hexFromLab(lab);
      const srgb = labToSrgb(lab);
      const overlay = getOverlayCanvas(mask, maskWidth, maskHeight, hex, {
        r: Math.round(srgb.r * 255),
        g: Math.round(srgb.g * 255),
        b: Math.round(srgb.b * 255),
      });
      ctx.save();
      ctx.globalAlpha = 0.5;
      ctx.drawImage(overlay, 0, 0, maskWidth, maskHeight, 0, 0, workW, workH);
      ctx.restore();
    }
  }, [
    image,
    previewData,
    previewWidth,
    previewHeight,
    hoverPreview,
    masks,
    maskWidth,
    maskHeight,
    visibleMasks,
    colors,
    repeat,
    repeatView,
    activeTab,
  ]);

  /* ---- Drag to pan (wrap-around) in repeat preview ---- */
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activeTab !== 'repeat') return;
    if (e.button !== 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, dx: repeat.dx, dy: repeat.dy };
    canvas.style.cursor = 'grabbing';
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    const canvas = canvasRef.current;
    if (!drag || !canvas) return;

    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0) return;
    const tileW = repeat.tileW || image.width || 1;
    const tileH = repeat.tileH || image.height || 1;
    // CSS px -> tile px (the canvas shows `tiles` tiles across).
    const perCssX = (tileW * tilesRef.current) / rect.width;
    const perCssY = (tileH * tilesRef.current) / rect.height;

    // Dragging right moves the artwork right, so the offset decreases.
    const dx = wrap(drag.dx - (e.clientX - drag.x) * perCssX, tileW);
    const dy = wrap(drag.dy - (e.clientY - drag.y) * perCssY, tileH);
    onRepeatChange({ dx, dy });
  };

  const endDrag = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.releasePointerCapture?.(e.pointerId);
      canvas.style.cursor = activeTab === 'repeat' ? 'grab' : 'default';
    }
  };

  return (
    <div className="canvas-inner">
      <canvas
        ref={canvasRef}
        className="preview-canvas"
        style={{ cursor: activeTab === 'repeat' ? 'grab' : 'default' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      />
    </div>
  );
}
